import { isRelease261Enabled } from '@/config/release261Features';
import { ASSOCIATION_TYPES_GDA_FILTER, ASSOCIATION_TYPES_VDA_FILTER } from '@/utils/associationTypeOptions';
import { DISEASE_MODEL_GDA, DISEASE_MODEL_VDA, SEX_OPTIONS } from '@/utils/diseaseModelOptions';
import { GTP_MESH_CLASSES } from '@/utils/gtpOptions';

// these dropdown lists are shared between 1+ columns
const diseaseTypeDropdownList = ['disease', 'phenotype'];
const semanticTypeDropdownList = [
  'Acquired Abnormality',
  'Anatomical Abnormality',
  'Behavior',
  'Cell Function',
  'Cell or Molecular Dysfunction',
  'Clinical Attribute',
  'Congenital Abnormality',
  'Disease or Syndrome',
  'Finding',
  'Individual Behavior',
  'Injury or Poisoning',
  'Laboratory or Test Result',
  'Mental or Behavioral Dysfunction',
  'Mental Process',
  'Neoplastic Process',
  'Organ or Tissue Function',
  'Organism Attribute',
  'Organism Function',
  'Pathologic Function',
  'Physiologic Function',
  'Sign or Symptom',
];
const diseaseClassDropdownListBase = [
  'Behavior and Behavior Mechanisms',
  'Cardiovascular Diseases',
  'Chemically-Induced Disorders',
  'Congenital, Hereditary, and Neonatal Diseases and Abnormalities ',
  'Digestive System Diseases',
  'Disorders of Environmental Origin',
  'Endocrine System Diseases',
  'Eye Diseases',
  'Hemic and Lymphatic Diseases',
  'Immune System Diseases',
  'Infections',
  'Mental Disorders',
  'Musculoskeletal Diseases',
  'Neoplasms',
  'Nervous System Diseases',
  'Nutritional and Metabolic Diseases',
  'Otorhinolaryngologic Diseases',
  'Pathological Conditions, Signs and Symptoms',
  'Psychological Phenomena',
  'Respiratory Tract Diseases',
  'Skin and Connective Tissue Diseases',
  'Stomatognathic Diseases',
  'Urogenital Diseases',
];
const diseaseClassDropdownListGda = [
  diseaseClassDropdownListBase[0],
  'Behavioral Disciplines and Activities',
  ...diseaseClassDropdownListBase.slice(1),
];
const diseaseClassDropdownListVda = [...diseaseClassDropdownListBase];
const geneTypeDropdownList = [
  'protein-coding',
  'ncRNA',
  'pseudo',
  'other',
  'biological-region',
  'snoRNA',
  'tRNA',
  'snRNA',
  'rRNA',
  'scRNA',
];
const proteinDropdownList = [
  'Calcium-binding protein',
  'Cell adhesion',
  'Cell-cell junction',
  'Cellular structure',
  'Chaperone',
  'Enzyme',
  'Enzyme modulator',
  'Epigenetic regulator',
  'Extracellular structure',
  'G-protein coupled receptor',
  'Immune response',
  'Ion channel',
  'Kinase',
  'Nuclear receptor',
  'Nucleic acid binding',
  'Receptor',
  'Signaling',
  'Storage',
  'Surfactant',
  'Transcription factor',
  'Transporter',
];
const hpoDropdownList = [
  'Abnormal cellular phenotype',
  'Abnormality of blood and blood-forming tissues',
  'Abnormality of connective tissue',
  'Abnormality of head or neck',
  'Abnormality of limbs',
  'Abnormality of metabolism/homeostasis',
  'Abnormality of prenatal development or birth',
  'Abnormality of the breast',
  'Abnormality of the cardiovascular system',
  'Abnormality of the digestive system',
  'Abnormality of the ear',
  'Abnormality of the endocrine system',
  'Abnormality of the eye',
  'Abnormality of the genitourinary system',
  'Abnormality of the immune system',
  'Abnormality of the integument',
  'Abnormality of the musculature',
  'Abnormality of the nervous system',
  'Abnormality of the respiratory system',
  'Abnormality of the skeletal system',
  'Abnormality of the thoracic cavity',
  'Abnormality of the voice',
  'Constitutional symptom',
  'Growth abnormality',
  'Neoplasm',
];
const doDropdownList = [
  'disease by infectious agent',
  'disease of anatomical entity',
  'disease of cellular proliferation',
  'disease of mental health',
  'disease of metabolism',
  'genetic disease',
  'physical disorder',
  'syndrome',
];
const ddaRelationDropdownList = [
  'has manifestation',
  'has associated morphology',
  'manifestation of',
  'associated morphology of',
  'is finding of disease',
  'due to',
  'has definitional manifestation',
  'has associated finding',
  'definitional manifestation of',
  'disease has finding',
  'cause of',
  'associated finding of',
];

const gdaSourcesListBase = [
  'ALL',
  'BIOBANK',
  'CLINICALTRIALS',
  'CLINGEN',
  'CLINPGX',
  'CLINVAR',
  ...(isRelease261Enabled ? (['CHEMBL'] as const) : []),
  'CURATED',
  'FINNGEN',
  'GENCC',
  'GWASCAT',
  'HPO',
  'INFERRED',
  'MGD_HUMAN',
  'MGD_MOUSE',
  'MODELS',
  'ORPHANET',
  'PHEWASCAT',
  'PSYGENET',
  'RGD_HUMAN',
  'RGD_RAT',
  'TEXTMINING_HUMAN',
  'TEXTMINING_MODELS',
  'UKBIOBANK',
  'UNIPROT',
];

const dropdownLists = {
  gdaSourcesList: gdaSourcesListBase,

  vdaSourcesList: [
    'ALL',
    'BIOBANK',
    'CLINGEN',
    'CLINPGX',
    'CLINVAR',
    'CURATED',
    'FINNGEN',
    'GWASCAT',
    'PHEWASCAT',
    'TEXTMINING_HUMAN',
    'UNIPROT',
    'UKBIOBANK',
  ],

  EL: [
    'Definitive',
    'Disputed',
    'Limited',
    'Moderate',
    'No Known Disease Relationship',
    'Refuted',
    'Strong',
    'Supportive',
  ],
  riskAllele: ['A', 'T', 'G', 'C'],
  evidenceLevelDropdownList: ['None', 'Definitive', 'Strong', 'Moderate', 'Disputed', 'Limited'],
  geneNcbiType: geneTypeDropdownList,
  ncbi_type: geneTypeDropdownList,
  geneProteinClassNames: proteinDropdownList,
  proteinClassIDs: proteinDropdownList,
  associationTypeGda: [...ASSOCIATION_TYPES_GDA_FILTER],
  associationTypeVda: [...ASSOCIATION_TYPES_VDA_FILTER],
  diseaseModelGda: [...DISEASE_MODEL_GDA],
  diseaseModelVda: [...DISEASE_MODEL_VDA],
  sex: [...SEX_OPTIONS],
  GTP: [...GTP_MESH_CLASSES],
  mostSevereConsequences: [
    'intron_variant',
    'intergenic_variant',
    'regulatory_region_variant',
    'stop_gained',
    'missense_variant',
    'non_coding_transcript_exon_variant',
    'synonymous_variant',
    '5_prime_UTR_variant',
    'splice_donor_variant',
    '3_prime_UTR_variant',
    'mature_miRNA_variant',
    'frameshift_variant',
    'splice_acceptor_variant',
    'TF_binding_site_variant',
    'splice_region_variant',
    'start_lost',
    'stop_lost',
    'inframe_deletion',
    'protein_altering_variant',
    'inframe_insertion',
    'coding_sequence_variant',
    'stop_retained_variant',
    'upstream_gene_variant',
    'downstream_gene_variant',
    'start_retained_variant',
    'transcript_ablation',
  ],
  ancestry_name: [
    'Israel (HGDP)',
    'Russia (HGDP)',
    'Aboriginal Australian ancestry',
    'Adygei in Caucasus',
    'Afghan',
    'African American',
    'African ancestry',
    'African Ancestry in Southwest US (1KGP)',
    'African Caribbean in Barbados (1KGP)',
    'Afro-Caribbean',
    'Akimel Oodham',
    'Albanian',
    'Aleut in Russia (SGDP)',
    'Algerian',
    'Altaian in Russia (SGDP)',
    'American',
    'American Indian',
    'Ami in Taiwan (SGDP)',
    'Amish',
    'Angolan',
    'Arab',
    'Arab Israeli founder',
    'Arab-American',
    'Argentine',
    'Arhuaco',
    'Armenian',
    'Ashkenazi Jew',
    'Asian',
    'Asian ancestry',
    'Asian-American',
    'Atayal in Taiwan (SGDP)',
    'Australia/New Zealand',
    'Australian',
    'Austrian',
    'Aymara',
    'Azerbaijani',
    'Bahamian',
    'Bahraini',
    'Balochi in Pakistan (HGDP)',
    'Bangladeshi',
    'Barbadian',
    'Bashkir',
    'Basque in France (HGDP)',
    'Bedouin in Negev',
    'Belarusian',
    'Belgian',
    'Belizean',
    'Bengali in Bangladesh (1KGP)',
    'Bengali in Bangladesh (SGDP)',
    'Beninese',
    'Bergamo in Italy(Bergamo) (SGDP)',
    'Bhutanese',
    'Biaka in Central African Republic (HGDP)',
    'Black',
    'Bolivian',
    'Brazilian',
    'British',
    'Bruneian',
    'Bulgarian',
    'Burkinabe',
    'Burmese',
    'Burundian',
    'California (1KGP)',
    'Cambodian',
    'Cameroonian',
    'Canadian',
    'Cape Verdean',
    'Central Asian ancestry',
    'Chadian',
    'Chechen in Russia (SGDP)',
    'Chicano',
    'Chilean',
    'China (1KGP)',
    'Chinese',
    'Chinese Dai in Xishuangbanna',
    'Colombia (1KGP)',
    'Colombian',
    'Colombian in Medellin',
    'Congolese',
    'Costa Rican',
    'Crete in Greece (SGDP)',
    'Croatian',
    'Cuban',
    'Czech',
    'Dai',
    'Danish',
    'Daur in China (HGDP)',
    'Dominican',
    'Druze in Carmel',
    'Dusun in Brunei (SGDP)',
    'Dutch',
    'Dutch founder',
    'East Asian ancestry',
    'Ecuadorian',
    'Emirati',
    'English in England (SGDP)',
    'Eritrean',
    'Esan in Nigeria (1KGP)',
    'Estonian',
    'Ethiopian',
    'European',
    'European American',
    'European ancestry',
    'Faroese',
    'Filipino',
    'Finnish',
    'Finnish founder',
    'Finnish in Finland (1KGP)',
    'French',
    'French Canadian founder',
    'French Polynesian',
    'Gabonese',
    'Gambian',
    'Georgian',
    'German',
    'Ghanaian',
    'Greater Middle Eastern  (Middle Eastern or North African or Persian)',
    'Greek',
    'Greenlander',
    'Guamanian',
    'Guatemalan',
    'Guinean',
    'Gujarati Indians in Houston',
    'Haitian',
    'Han Chinese',
    'Han Chinese in Beijing',
    'Han Chinese South (1KGP)',
    'Han in China (HGDP)',
    'Hazara in Pakistan (HGDP)',
    'Hispanic',
    'Hispanic or Latin',
    'Honduran',
    'Hong Kong Chinese',
    'Hui',
    'Hungarian',
    'Hutterite',
    'Iberian populations in Spain (1KGP)',
    'Icelandic',
    'Indian',
    'Indian Telugu in the UK (1KGP)',
    'Indigenous American',
    'Indigenous in North America',
    'Indigenous in South America',
    'Indigenous Siberian ancestry',
    'Indonesian',
    'Inuit',
    'Iranian',
    'Iraqi',
    'Iraqi Jew in Iraq (SGDP)',
    'Irish',
    'Israeli',
    'Italian',
    'Italian founder',
    'Jamaican',
    'Japanese',
    'Jewish Israeli',
    'Jordanian',
    'Kapu in India (SGDP)',
    'Kazakhstani',
    'Kenyan',
    'Kinh in Ho Chi Minh City',
    'Kinh in Vietnam (SGDP)',
    'Korean',
    'Kosovar',
    'Kuwaiti',
    'Kyrgyz in Kyrgyzystan (SGDP)',
    'Lao',
    'Latin or Admixed American',
    'Latin or Admixed American ancestry',
    'Latino',
    'Latvian',
    'Lebanese',
    'Libyan',
    'Lithuanian',
    'Luo in Kenya (SGDP)',
    'Macedonian',
    'Malawian',
    'Malaysian',
    'Malaysian Chinese',
    'Maldivian',
    'Malian',
    'Maltese',
    'Mandenka in Senegal (HGDP)',
    'Maori in New Zealand (SGDP)',
    'Mapuche',
    'Martinican',
    'Masai in Kenya (1KGP)',
    'Mauritanian',
    'Mauritian',
    'Maya',
    'Mayan in Mexico (SGDP)',
    'Mbuti in Democratic Republic of Congo (HGDP)',
    'Mende in Sierra Leone (1KGP)',
    'Mende in Sierra Leone (SGDP)',
    'Mexican',
    'Mexican Ancestry in Los Angeles',
    'Miao in China (HGDP)',
    'Micronesian',
    'Middle Eastern',
    'Mixe in Mexico (SGDP)',
    'Mixteco',
    'Mongolian',
    'Montenegrin',
    'Moroccan',
    'Mozambican',
    'Nahua',
    'Native Hawaiian',
    'Nauruan',
    'Naxi in China (HGDP)',
    'Nepalese',
    'New Caledonian',
    'New Zealander',
    'Nicaraguan',
    'Nigerian',
    'Northern Finnish founder',
    'Northern Han in China (HGDP)',
    'Northern Irish',
    'Norwegian',
    'Old Order Amish',
    'Omani',
    'Otomi',
    'Pacific Islander',
    'Pakistani',
    'Palauan',
    'Palestinian',
    'Panamanian',
    'Papua New Guinean',
    'Papuan in Papua New Guinea (SGDP)',
    'Paraguayan',
    'Pathan in Pakistan (HGDP)',
    'Persian',
    'Peru (1KGP)',
    'Peruvian',
    'Peruvian in Lima',
    'Plains Indians',
    'Polish',
    'Puerto Rican',
    'Puerto Rican in Puerto Rico (1KGP)',
    'Punjabi in Pakistan (SGDP)',
    'Punjabi Sikh',
    'Qatari',
    'Quechua',
    'Romanian',
    'Russian',
    'Rwandan',
    'Saami in Finland (SGDP)',
    'Saharawi in Western Sahara (Morocco) (SGDP)',
    'Samaritan in Israel (SGDP)',
    'Samoan',
    'Sardinian',
    'Saudi',
    'Scottish',
    'Senegalese',
    'Serb',
    'Silk Road founder',
    'Sindhi in Pakistan (HGDP)',
    'Singaporean Chinese',
    'Slovak',
    'Slovene',
    'Somali in Kenya (SGDP)',
    'South African',
    'South Asian ancestry',
    'South East Asian ancestry',
    'South Tyrolean',
    'Spanish',
    'Sri Lankan',
    'Sri Lankan Tamil in the UK (1KGP)',
    'Sub-Saharan African',
    'Sudanese',
    'Surinamese',
    'Surui',
    'Surui in Brazil (HGDP)',
    'Swedish',
    'Swiss',
    'Syrian',
    'Taiwanese',
    'Taiwanese Han',
    'Tajik in Tajikistan (SGDP)',
    'Tanzanian',
    'Tatar',
    'Thai',
    'Tibetan',
    'Tlingit in Russia (SGDP)',
    'Togolese',
    'Toscani in Italy (1KGP)',
    'Tujia in China (HGDP)',
    'Tunisian',
    'Turkish',
    'Turkmen',
    'TX (1KGP)',
    'Ugandan',
    'Ukrainian',
    'Uruguayan',
    'Utah residents (CEPH) with Northern and Western European ancestry  (1KGP)',
    'Uygur',
    'Venezuelan',
    'Vietnam (1KGP)',
    'Vietnamese',
    'Welsh',
    'Yakut in Russia (SGDP)',
    'Yemeni',
    'Yemenite Jew in Yemen (SGDP)',
    'Yoruba in Nigeria (HGDP)',
    'Zambian',
  ],
  disease1ClassName_UMLS: semanticTypeDropdownList,
  disease2ClassName_UMLS: semanticTypeDropdownList,
  diseaseClassName_UMLS: semanticTypeDropdownList,
  diseaseClassNameMshGda: diseaseClassDropdownListGda,
  diseaseClassNameMshVda: diseaseClassDropdownListVda,
  disease1ClassName_MSH: diseaseClassDropdownListGda,
  disease2ClassName_MSH: diseaseClassDropdownListGda,
  disease1_Type: diseaseTypeDropdownList,
  disease2_Type: diseaseTypeDropdownList,
  diseaseType: diseaseTypeDropdownList,
  type: diseaseTypeDropdownList,
  disease1ClassName_HPO: hpoDropdownList,
  disease2ClassName_HPO: hpoDropdownList,
  diseaseClassName_HPO: hpoDropdownList,
  disease1ClassName_DO: doDropdownList,
  disease2ClassName_DO: doDropdownList,
  diseaseClassName_DO: doDropdownList,
  DBSNPclass: ['snv', 'delins', 'del', 'mnv', 'ins'],
  gof_lof: ['GOF', 'LOF'],
  ddaRelation: ddaRelationDropdownList,
  reference_type: ['PMID', 'NCTID'],
  nct_phase: ['PHASE2', 'PHASE1', 'PHASE3', 'PHASE4', 'PHASE1/PHASE2', 'EARLY_PHASE1', 'PHASE2/PHASE3'],
  chemical_effect: ['Dosage', 'Efficacy', 'Metabolism/PK', 'other', 'PD', 'therapeutic', 'toxicity'],
  gender: ['Both', 'None'],
  disease_inheritance: [
    'Autosomal dominant',
    'Autosomal recessive',
    'X-linked dominant',
    'X-linked recessive',
    'Chromosomal',
    'Mitochondrial inheritance',
    'Multigenic/multifactorial',
    'Oligogenic',
    'Semi-dominant',
    'Y-linked',
    'No data available',
    'Not applicable',
    'Not yet documented',
  ],
  disease_prevalence_class: [
    '>1 / 1000',
    '1-5 / 10 000',
    '6-9 / 10 000',
    '1-9 / 100 000',
    '1-9 / 1 000 000',
    '<1 / 1 000 000',
    'Not yet documented',
    'Unknown',
  ],
  disease_prevalence_type: [
    'Point prevalence',
    'Prevalence at birth',
    'Lifelong prevalence',
    'Annual Incidence',
    'Cases/families',
  ],
  chemMoleculeType: [
    'Small molecule',
    'Protein',
    'Unknown',
    'Antibody',
    'Oligosaccharide',
    'Antibody drug conjugate',
    'Oligonucleotide',
    'Enzyme',
    'Gene',
    'Cell',
  ],
  clinical_significance: [
    'affects',
    'association',
    'confers sensitivity',
    'conflicting classifications of pathogenicity',
    'drug response',
    'established risk allele',
    'pathogenic',
    'likely pathogenic',
    'likely risk allele',
    'no classifications from unflagged records',
    'other',
    'pathogenic, low penetrance',
    'protective',
    'risk factor',
    'uncertain risk allele',
  ],
  association: ['Positive', 'Negative'],
};

export default dropdownLists;
